import { computed, ref } from "vue";

interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface NavigatorLike {
    userAgent: string;
    platform: string;
    maxTouchPoints: number;
    standalone?: boolean;
}

const deferredPrompt = ref< BeforeInstallPromptEvent | null >( null );
const canInstall = computed< boolean >( () => deferredPrompt.value !== null );

window.addEventListener( "beforeinstallprompt", ( event: Event ) => {
    event.preventDefault();
    deferredPrompt.value = event as BeforeInstallPromptEvent;
} );

window.addEventListener( "appinstalled", () => {
    deferredPrompt.value = null;
} );

export function detectIosSafari( nav: NavigatorLike = navigator as NavigatorLike ): boolean {
    const ua = nav.userAgent;

    const isIosDevice = /iPhone|iPad|iPod/.test( ua )
        || ( nav.platform === "MacIntel" && nav.maxTouchPoints > 1 );

    if ( !isIosDevice ) return false;

    // Exclude non-Safari browsers on iOS (they all embed WebKit but aren't Safari)
    const nonSafariBrowsers = /CriOS|FxiOS|OPiOS|EdgiOS|Chrome/;
    return !nonSafariBrowsers.test( ua );
}

export function detectStandalone(
    nav: NavigatorLike = navigator as NavigatorLike,
    win: Window = window
): boolean {
    if ( nav.standalone === true ) return true;
    return win.matchMedia( "(display-mode: standalone)" ).matches;
}

const isIosSafari = detectIosSafari();
const isStandalone = detectStandalone();
const canShowIosInstall = computed< boolean >( () => isIosSafari && !isStandalone );

const showIosInstructions = ref< boolean >( false );

function openIosInstallInstructions(): void {
    showIosInstructions.value = true;
}

function closeIosInstallInstructions(): void {
    showIosInstructions.value = false;
}

export function usePwaInstall() {
    async function installPwa(): Promise< void > {
        const prompt = deferredPrompt.value;
        if ( !prompt ) return;

        await prompt.prompt();
        const { outcome } = await prompt.userChoice;

        if ( outcome === "accepted" ) {
            deferredPrompt.value = null;
        }
    }

    return {
        canInstall,
        installPwa,
        canShowIosInstall,
        showIosInstructions,
        openIosInstallInstructions,
        closeIosInstallInstructions
    };
}
